/**
 * Helper: Algolia Client
 * -----------------------------------------------------------------------------
 * Shared singleton Algolia client for all components.
 * - Initializes once and reuses connection across components.
 * - Includes distinct wrapper for deduplication.
 * - Request batching for improved performance.
 *
 */
// eslint-disable-next-line import/named
import { markRaw } from 'vue'
import algoliasearch from 'algoliasearch/lite'
import { history as historyRouter } from 'instantsearch.js/es/lib/routers'

let sharedClient = null
let distinctSearchClient = null
let rawSearchClient = null
let missingMarketIndex = null

const MISSING_MARKET_INDEX_KEY = 'cnvs:algolia-missing-market-index'

/**
 * Empty response for when Algolia is not configured.
 */
const emptyResponse = (requests) => Promise.resolve({
  results: (requests || []).map(() => ({
    hits: [],
    nbHits: 0,
    nbPages: 0,
    page: 0,
    processingTimeMS: 0,
    hitsPerPage: 0,
    exhaustiveNbHits: false,
    query: '',
    params: '',
  })),
})

/**
 * Extract redirect URL from Algolia search result.
 * Handles both Visual Editor and Manual Editor rule formats.
 * @param {Object} result - Single Algolia search result object.
 * @returns {String|null} Redirect URL or null if not present.
 */
export function getAlgoliaRedirectUrl(result) {
  if (!result) {
    return null
  }

  // Visual Editor rule: renderingContent.redirect.url
  let redirectUrl = result?.renderingContent?.redirect?.url

  // Manual Editor rule: userData is an array, find item with redirect
  if (!redirectUrl && Array.isArray(result?.userData)) {
    const redirectData = result.userData.find(
      (item) => Boolean(item?.redirect),
    )
    const redirect = redirectData?.redirect
    redirectUrl = typeof redirect === 'string' ? redirect : redirect?.url
  }

  return redirectUrl || null
}

/**
 * Get or create the shared Algolia client.
 * @returns {Object|null} Algolia client or null if not configured.
 */
export function getAlgoliaClient() {
  if (sharedClient !== null) {
    return sharedClient
  }

  const algoliaConfig = window.cnvs?.algolia || {}
  const credentials = algoliaConfig.credentials || {}

  if (!credentials.appId || !credentials.apiKey) {
    sharedClient = false
    return null
  }

  sharedClient = withMarketIndexFallback(algoliasearch(credentials.appId, credentials.apiKey))
  return sharedClient
}

/**
 * Fall back to the base index when the market index doesn't exist.
 * - Algolia rejects the whole multi-query if one index is missing, which
 *   leaves the PLP/search grid stuck on its loading state for that market.
 * @param {Object} client - Algolia client.
 * @returns {Object}
 */
function withMarketIndexFallback(client) {
  const search = client.search.bind(client)
  const searchForFacetValues = client.searchForFacetValues?.bind(client)

  client.search = (requests, ...args) => {
    return requestWithFallback(search, requests, args)
  }

  if (searchForFacetValues) {
    client.searchForFacetValues = (requests, ...args) => {
      return requestWithFallback(searchForFacetValues, requests, args)
    }
  }

  return client
}

/**
 * Run request, retrying against the base index if the market index is missing.
 * @param {Function} request - Client request method.
 * @param {Array} requests - Multi-index requests.
 * @param {Array} args - Additional request arguments.
 * @returns {Promise<Object>}
 */
async function requestWithFallback(request, requests, args) {
  try {
    return await request(mapToAvailableIndices(requests), ...args)
  } catch (error) {
    const marketIndex = getMarketIndex()
    const isMissingMarketIndex =
      error?.status === 404 &&
      marketIndex !== getBaseIndex() &&
      String(error?.message || '').includes(marketIndex)

    if (!isMissingMarketIndex || isMarketIndexMissing()) {
      throw error
    }

    setMarketIndexMissing(marketIndex)
    return request(mapToAvailableIndices(requests), ...args)
  }
}

/**
 * Swap market index names for base index names when the market is missing.
 * @param {Array} requests - Multi-index requests.
 * @returns {Array}
 */
function mapToAvailableIndices(requests) {
  if (!Array.isArray(requests) || !isMarketIndexMissing()) {
    return requests
  }

  const baseIndex = getBaseIndex()
  const marketIndex = getMarketIndex()

  return requests.map((request) => {
    if (!request?.indexName?.startsWith(marketIndex)) {
      return request
    }

    return {
      ...request,
      indexName: `${baseIndex}${request.indexName.slice(marketIndex.length)}`,
    }
  })
}

/**
 * Get base products index name.
 * @returns {String}
 */
function getBaseIndex() {
  return window.cnvs?.algolia?.indices?.products || 'shopify_products'
}

/**
 * Get market products index name, regardless of whether it exists.
 * @returns {String}
 */
function getMarketIndex() {
  return resolveMarketProductsIndex(getBaseIndex())
}

/**
 * Check if the current market's index is known to be missing.
 * - Persisted for the session so later page loads use the base index up front.
 * @returns {Boolean}
 */
function isMarketIndexMissing() {
  if (missingMarketIndex === null) {
    try {
      missingMarketIndex = sessionStorage.getItem(MISSING_MARKET_INDEX_KEY) || ''
    } catch {
      missingMarketIndex = ''
    }
  }

  const marketIndex = getMarketIndex()

  return marketIndex !== getBaseIndex() && missingMarketIndex === marketIndex
}

/**
 * Flag market index as missing.
 * @param {String} marketIndex - Market index name.
 */
function setMarketIndexMissing(marketIndex) {
  missingMarketIndex = marketIndex

  try {
    sessionStorage.setItem(MISSING_MARKET_INDEX_KEY, marketIndex)
  } catch {
    // Silently fail if session storage is unavailable
  }
}

/**
 * Get a search client wrapper with distinct enabled for deduplication.
 * - Algolia Shopify integration indexes one record per variant,
 *   not per product.
 * - `distinct: true` deduplicates server-side using
 *   `attributeForDistinct`.
 * - Batches multiple search calls within the same into a single API request.
 * @returns {Object} Search client suitable for ais-instant-search.
 */
export function getDistinctSearchClient() {
  if (distinctSearchClient !== null) {
    return distinctSearchClient
  }

  const client = getAlgoliaClient()

  if (!client) {
    distinctSearchClient = markRaw({
      search: emptyResponse,
      searchForFacetValues: emptyResponse,
    })
    return distinctSearchClient
  }

  let pendingRequests = null
  let batchTimer = null

  distinctSearchClient = markRaw({
    search: (requests) => {
      const modifiedRequests = requests.map((request) => ({
        ...request,
        params: {
          ...request.params,
          distinct: true,
        },
      }))

      return new Promise((resolve, reject) => {
        if (!pendingRequests) {
          pendingRequests = []
        }

        pendingRequests.push({ requests: modifiedRequests, resolve, reject })

        if (batchTimer) {
          clearTimeout(batchTimer)
        }

        batchTimer = setTimeout(() => {
          const batch = pendingRequests
          pendingRequests = null
          batchTimer = null

          const allRequests = batch.flatMap((b) => b.requests)

          client.search(allRequests).then((response) => {
            // Redirect if Algolia rule exists (before results render)
            const redirectUrl = getAlgoliaRedirectUrl(response.results?.[0])
            if (redirectUrl) {
              window.location.href = redirectUrl
              return
            }

            let offset = 0
            batch.forEach((b) => {
              const count = b.requests.length
              b.resolve({
                results: response.results.slice(offset, offset + count),
              })
              offset += count
            })
          }).catch((error) => {
            batch.forEach((b) => b.reject(error))
          })
        }, 0)
      })
    },
    searchForFacetValues: client.searchForFacetValues
      ? (...args) => client.searchForFacetValues(...args)
      : emptyResponse,
  })

  return distinctSearchClient
}

/**
 * Get the raw Algolia client (without distinct wrapper).
 * - Useful for predictive search and other multi-index searches.
 * @returns {Object|null} Raw Algolia client wrapped for Vue reactivity.
 */
export function getRawSearchClient() {
  if (rawSearchClient !== null) {
    return rawSearchClient
  }

  const client = getAlgoliaClient()

  if (!client) {
    rawSearchClient = null
    return null
  }

  rawSearchClient = markRaw(client)
  return rawSearchClient
}

/**
 * Get Algolia configuration.
 * @returns {Object} Algolia config with indices and sort orders.
 */
export function getAlgoliaConfig() {
  const algoliaConfig = window.cnvs?.algolia || {}
  const baseIndex = algoliaConfig.indices?.products || 'shopify_products'
  const mainIndex = isMarketIndexMissing()
    ? baseIndex
    : resolveMarketProductsIndex(baseIndex)

  return {
    mainIndex,
    sortOrders: normalizeSortOrderIndices(algoliaConfig.sortOrders || [], baseIndex, mainIndex),
    indices: algoliaConfig.indices || {},
  }
}

/**
 * Resolve the market-aware products index from the base products index.
 * - Expected pattern from Algolia Shopify Markets indexing:
 *   `<baseIndex>_<country_name_snake_case>_<language_code>`
 * - Example: `shopify_products_united_states_en`
 * @param {String} baseIndex - Base products index name.
 * @returns {String}
 */
function resolveMarketProductsIndex(baseIndex) {
  const store = window.cnvs?.store || {}
  const marketHandleSlug = toMarketSlug(store.marketHandle)
  const marketNameSlug = toMarketSlug(store.marketName)
  const isPrimaryMarket = parseMarketPrimaryFlag(store)
  const languageCode = normalizeLanguageCode(store.language)
  const countryNameSlug = toMarketSlug(store.countryName)
  const currencyCode = String(store.currency || '').trim().toUpperCase()
  const baseCurrencyCode = String(store.baseCurrency || '').trim().toUpperCase()

  // Algolia market suffixes align with market handle in Shopify Markets setup.
  // Market name is used only as a final fallback when handle is unavailable.
  const marketSuffix = marketHandleSlug || marketNameSlug

  // Safety guard for primary/base market when Shopify primary flag is not
  // reliably exposed to storefront JS.
  const isLikelyBaseMarket =
    Boolean(countryNameSlug) &&
    marketSuffix === countryNameSlug &&
    currencyCode &&
    baseCurrencyCode &&
    currencyCode === baseCurrencyCode

  // Primary market keeps the base index in Algolia Markets indexing.
  if (isPrimaryMarket || isLikelyBaseMarket || !marketSuffix || !languageCode) {
    return baseIndex
  }

  return `${baseIndex}_${marketSuffix}_${languageCode}`
}

/**
 * Normalize storefront language to Algolia market suffix format.
 * - Converts values like en-GB / en_GB to en.
 * @param {*} language - Storefront language value.
 * @returns {String}
 */
function normalizeLanguageCode(language) {
  const raw = String(language || '').trim()

  if (!raw) {
    return ''
  }

  const [baseLanguage] = raw.split(/[-_]/)
  return toMarketSlug(baseLanguage)
}

/**
 * Parse primary-market flag from store data.
 * @param {Object} store - Store object from theme variables.
 * @returns {Boolean}
 */
function parseMarketPrimaryFlag(store) {
  const value = store?.isPrimaryMarket

  return value === true || value === 'true' || value === 1 || value === '1'
}

/**
 * Slugify market labels for Algolia index naming.
 * @param {*} value - Raw value.
 * @returns {String}
 */
function toMarketSlug(value) {
  if (!value) {
    return ''
  }

  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Normalize sort order index names to the active market index.
 * - Handles values stored as:
 *   - `products_virtual_price_asc`
 *   - `shopify_products_virtual_price_asc`
 *   - already market-scoped index values
 * @param {Array} sortOrders - Raw sort orders.
 * @param {String} baseIndex - Base products index name.
 * @param {String} mainIndex - Market-aware products index name.
 * @returns {Array}
 */
function normalizeSortOrderIndices(sortOrders, baseIndex, mainIndex) {
  return sortOrders.map((sortOrder) => {
    const value = String(sortOrder?.value || '')

    if (!value) {
      return sortOrder
    }

    // Already market-aware.
    if (value.startsWith(`${mainIndex}_`)) {
      return sortOrder
    }

    // Full base index + replica suffix.
    if (value.startsWith(`${baseIndex}_`)) {
      const suffix = value.slice(baseIndex.length)
      return {
        ...sortOrder,
        value: `${mainIndex}${suffix}`,
      }
    }

    // Replica suffix configured without prefix in settings.
    if (value.startsWith('products_')) {
      const suffix = value.slice('products'.length)
      return {
        ...sortOrder,
        value: `${mainIndex}${suffix}`,
      }
    }

    return sortOrder
  })
}

/**
 * Get active filters from theme settings.
 * - Reads filters configured in Shopify theme settings (Algolia Filter Options)
 * - Filters can reference standard Algolia attributes or custom metafields
 * - Example: { label: 'Dresser', attribute: 'meta.custom.dresser' }
 * - Returns empty array if no filters configured
 * @returns {Array} Array of filter objects with { label, attribute }
 */
export function getActiveFilters() {
  const filterOptions = window.cnvs?.algolia?.filterOptions
  return Array.isArray(filterOptions) ? filterOptions : []
}

/**
 * Handle Algolia redirect rule redirect.
 * - Checks for stored redirect URL from Algolia rule
 * - Clears the stored URL and redirects if present
 * - Returns true if redirect occurred, false otherwise
 * @returns {Boolean} True if redirect was performed, false otherwise
 */
export function handleAlgoliaRedirect() {
  if (window.algoliaRedirectUrl) {
    const redirectUrl = window.algoliaRedirectUrl
    window.algoliaRedirectUrl = null
    window.location.href = redirectUrl
    return true
  }
  return false
}

/**
 * Get the initial Algolia page from the URL.
 * @returns {Number}
 */
export function getInitialAlgoliaPage() {
  if (typeof window === 'undefined') {
    return 1
  }

  return parsePageNumber(new URLSearchParams(window.location.search).get('page'))
}

/**
 * Slugify a filter label for use in URL parameters.
 * - Converts to lowercase and removes special characters
 * - Preserves word boundaries by replacing spaces with underscores
 * @param {String} label - Filter label from theme settings.
 * @returns {String} URL-safe parameter name.
 */
function slugifyFilterLabel(label) {
  if (!label) {
    return ''
  }

  return String(label)
    .toLowerCase()
    // Replace spaces with underscores
    .replace(/\s+/g, '_')
    // Remove special characters
    .replace(/[^a-z0-9_-]/g, '')
    // Remove leading/trailing underscores
    .replace(/^_+|_+$/g, '')
}

/**
 * Create a bidirectional map between filter labels and attributes.
 * Maps slugified labels to filter options for URL parameter lookup.
 * @param {Array} filterOptions - Array of filter option objects.
 * @returns {Object} Map with label->attribute mappings.
 */
function createFilterLabelMap(filterOptions) {
  const map = {}
  filterOptions.forEach((filterOption) => {
    const slugifiedLabel = slugifyFilterLabel(filterOption.label)
    if (slugifiedLabel) {
      map[slugifiedLabel] = filterOption
    }
  })
  return map
}

/**
 * Create a bidirectional map between sort order labels and index values.
 * Maps slugified labels to sort order index values for URL parameter lookup.
 * @param {Array} sortOrders - Array of sort order objects with { value,
 * label }.
 * @returns {Object} Map with label->value and value->label mappings.
 */
function createSortOrderMap(sortOrders) {
  const map = {
    labelToValue: {},
    valueToLabel: {},
  }
  sortOrders.forEach((sortOrder) => {
    const slugifiedLabel = slugifyFilterLabel(sortOrder.label)
    if (slugifiedLabel && sortOrder.value) {
      map.labelToValue[slugifiedLabel] = sortOrder.value
      map.valueToLabel[sortOrder.value] = slugifiedLabel
    }
  })
  return map
}

/**
 * Build InstantSearch routing config with readable URL parameters.
 * @param {String} indexName - Algolia index name.
 * @param {Object} options - Routing options.
 * @param {Array} options.filterOptions - Active Algolia filter options.
 * @param {Array} options.sortOrders - Sort order options from theme settings.
 * @param {String} options.defaultSortIndex - Default Algolia sort index.
 * @param {Boolean} options.includeQuery - Whether to map the q parameter.
 * @returns {Object}
 */
export function createAlgoliaRoutingConfig(
  indexName,
  { defaultSortIndex = indexName, filterOptions = [], sortOrders = [], includeQuery = false } = {},
) {
  // Create maps for URL parameter lookup
  const filterLabelMap = createFilterLabelMap(filterOptions)
  const sortOrderMap = createSortOrderMap(sortOrders)

  return {
    router: historyRouter({
      cleanUrlOnDispose: false,
      writeDelay: 400,
    }),
    stateMapping: {
      stateToRoute(uiState) {
        const indexUiState = uiState[indexName] || {}
        const routeState = {}
        const configureQuery = indexUiState.configure?.query

        if (includeQuery && configureQuery) {
          routeState.q = configureQuery
        }

        if (indexUiState.sortBy && indexUiState.sortBy !== defaultSortIndex) {
          // Use slugified sort label for readable URL
          const slugifiedSortLabel = sortOrderMap.valueToLabel[indexUiState.sortBy]
          if (slugifiedSortLabel) {
            routeState.sort_by = slugifiedSortLabel
          } else {
            // Fallback to original value if not found in map (shouldn't happen)
            routeState.sort_by = indexUiState.sortBy
          }
        }

        if (indexUiState.page && Number(indexUiState.page) > 1) {
          routeState.page = Number(indexUiState.page)
        }

        if (indexUiState.toggle?.inventory_available) {
          routeState.availability = '1'
        }

        if (indexUiState.range?.price) {
          routeState.price = indexUiState.range.price
        }

        filterOptions.forEach((filterOption) => {
          const attribute = filterOption.attribute
          const refinement = indexUiState.refinementList?.[attribute]

          if (refinement && refinement.length > 0) {
            // Use slugified label as URL parameter name for readability
            const paramName = slugifyFilterLabel(filterOption.label)
            if (paramName) {
              routeState[paramName] = refinement
            }
          }
        })

        return routeState
      },

      routeToState(routeState) {
        const indexUiState = {}
        const refinementList = {}

        // Define known non-filter URL parameters
        const nonFilterParams = new Set(['q', 'sort_by', 'page', 'availability', 'price', 'type'])

        const query = getFirstRouteValue(routeState.q)
        const sortByLabel = getFirstRouteValue(routeState.sort_by)
        const price = getFirstRouteValue(routeState.price)

        if (includeQuery && query) {
          indexUiState.configure = {
            query,
          }
        }

        // Map slugified sort label back to actual Algolia index value
        if (sortByLabel) {
          const sortByValue = sortOrderMap.labelToValue[sortByLabel]
          if (sortByValue) {
            indexUiState.sortBy = sortByValue
          } else {
            // Fallback: check if it's already an index value
            indexUiState.sortBy = sortByLabel
          }
        }

        const page = parsePageNumber(routeState.page)
        if (page > 1) {
          indexUiState.page = page
        }

        if (routeState.availability === '1' || routeState.availability === true) {
          indexUiState.toggle = {
            inventory_available: true,
          }
        }

        if (price) {
          indexUiState.range = {
            price,
          }
        }

        // Map URL parameters back to filter attributes using the
        // label map. Each URL parameter name is a slugified filter label
        // that maps to an Algolia filter option containing the actual
        // attribute name.
        Object.entries(routeState).forEach(([paramName, value]) => {
          // Skip non-filter parameters (q, sort, page, price,
          // availability, etc.)
          if (nonFilterParams.has(paramName)) {
            return
          }

          // Look up the filter option by slugified label from the URL parameter
          const filterOption = filterLabelMap[paramName]
          if (filterOption) {
            // Use the actual Algolia attribute for the state
            const attribute = filterOption.attribute
            refinementList[attribute] = getRouteValues(value)
          }
        })

        if (Object.keys(refinementList).length > 0) {
          indexUiState.refinementList = refinementList
        }

        return {
          [indexName]: indexUiState,
        }
      },
    },
  }
}

/**
 * Normalize a route value to the first available string.
 * @param {*} value - Route value.
 * @returns {String}
 */
function getFirstRouteValue(value) {
  if (Array.isArray(value)) {
    return value[0]
  }

  return value
}

/**
 * Normalize a route value to an array of usable strings.
 * @param {*} value - Route value.
 * @returns {Array}
 */
function getRouteValues(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean)
  }

  return [value].filter(Boolean)
}

/**
 * Parse a URL page value into InstantSearch's 1-based page state.
 * @param {*} value - Page value from the route.
 * @returns {Number}
 */
function parsePageNumber(value) {
  const page = Number(value)
  return Number.isInteger(page) && page > 0 ? page : 1
}

/**
 * Build price filter active state from Algolia refinement.
 * - Converts decimal dollars to cents for storage.
 * - Generates formatted label for display.
 * - Called on every render to sync URL-loaded price filters.
 * @param {Object} priceRefinement - Current price range from Algolia.
 * @param {Object} priceRange - Price range stats from Algolia.
 * @param {Function} formatMoney - Vue plugin method to format currency.
 * @returns {Object|null} Price filter object or null if no refinement.
 */
export function buildPriceFilterState(priceRefinement, priceRange, formatMoney) {
  if (typeof priceRefinement?.min !== 'number' && typeof priceRefinement?.max !== 'number') {
    return null
  }

  const minCents = priceRefinement.min ? Math.round(priceRefinement.min * 100) : null
  const maxCents = priceRefinement.max ? Math.round(priceRefinement.max * 100) : null

  return {
    paramName: 'filter.v.price',
    value: `${priceRefinement.min || ''}-${priceRefinement.max || ''}`,
    label: `${formatMoney(minCents || 0)} - ${formatMoney(maxCents || (priceRange?.max * 100) || 0)}`,
    count: null,
    active: true,
    minValueCents: minCents,
    maxValueCents: maxCents,
  }
}
